import type { Action, Rule, Scope, VariableValue } from '@sw/schema'
import type { LatchState } from './latch.js'

/** Per-trigger memory. Which fields matter depends on the trigger type. */
export interface TriggerState {
  latch?: LatchState
  /** Dwell: when the listener became continuously present. */
  insideSinceMs?: number
  dwellFired?: boolean
  /** Speed and motion: when the threshold first held. */
  conditionSinceMs?: number
  sustainedFired?: boolean
  /** Chainage: previous distance along the course, to detect the crossing. */
  previousChainageM?: number
  /** One-shot triggers such as elapsed and session start. */
  fired?: boolean
  /** Random: when it last fired, for the minimum interval. */
  lastFiredMs?: number
  /** Signal-consuming triggers: the newest signal already accounted for. */
  lastSeenMs?: number
  /** Wall clock: the minute already fired, so a schedule fires once. */
  lastMinuteIndex?: number
  sequenceProgress?: number
  sequenceStartedMs?: number
  /** Variable and motion: the previous truth, so a rule fires on change. */
  previousTruth?: boolean
}

export interface RuleState {
  /** Runtime enablement, which other rules and operators can change. */
  enabled: boolean
  fireCount: number
  lastFiredAtMs?: number
  triggers: TriggerState[]
  /** When each trigger last hit, for `all` mode. */
  lastHitMs: (number | undefined)[]
}

export interface PendingAction {
  ruleId: string
  action: Action
  atMs: number
  /** Preserves authored order among actions that come due together. */
  orderInRule: number
}

export interface Firing {
  ruleId: string
  atMs: number
}

export interface EngineState {
  scope: Scope
  startedAtMs: number
  lastTickMs?: number
  previousAccuracyDegraded: boolean
  rules: Record<string, RuleState>
  variables: Record<string, VariableValue>
  /** Recent rule firings, feeding chained and sequence triggers. */
  firings: Firing[]
  pending: PendingAction[]
}

export function createRuleState(rule: Rule): RuleState {
  return {
    enabled: rule.enabled,
    fireCount: 0,
    triggers: rule.triggers.map(() => ({})),
    lastHitMs: rule.triggers.map(() => undefined),
  }
}

export interface EngineStateOptions {
  startedAtMs?: number
  /** Seed values, e.g. session variables synced down to a listener's device. */
  variables?: Readonly<Record<string, VariableValue>>
}

export function createEngineState(
  rules: readonly Rule[],
  declaredVariables: readonly { name: string; initial: VariableValue }[],
  scope: Scope,
  options: EngineStateOptions = {},
): EngineState {
  const variables: Record<string, VariableValue> = {}
  for (const declaration of declaredVariables) variables[declaration.name] = declaration.initial
  Object.assign(variables, options.variables ?? {})

  const ruleStates: Record<string, RuleState> = {}
  for (const rule of rules) ruleStates[rule.id] = createRuleState(rule)

  return {
    scope,
    startedAtMs: options.startedAtMs ?? 0,
    previousAccuracyDegraded: false,
    rules: ruleStates,
    variables,
    firings: [],
    pending: [],
  }
}
